import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { useSignal } from '@backstage/plugin-signals-react';
import { knowledgeApiRef } from '../../api';
import { KnowledgeChatContext } from './ChatContext';
import type {
  KnowledgeBuilderFormData,
  KnowledgeBuilderTemplateParameters,
  KnowledgeBuilderUiOptions,
  KnowledgeCapabilities,
  KnowledgeDocument,
  KnowledgeMessage,
  KnowledgeMessageAttachment,
  KnowledgeProgressEvent,
  ProposedDraftChange,
  KnowledgeSession,
  KnowledgeSource,
  KnowledgeValidationResult,
} from '../../api';
import { buildKnowledgeReviewReadiness } from './reviewReadiness';

type SaveState = 'idle' | 'saving' | 'saved' | 'offline' | 'error';

interface KnowledgeBuilderContextValue {
  session?: KnowledgeSession;
  activeDocument?: KnowledgeDocument;
  activeDocumentPath?: string;
  messages: KnowledgeMessage[];
  sources: KnowledgeSource[];
  documents: KnowledgeDocument[];
  directories: string[];
  saveState: SaveState;
  validation?: KnowledgeValidationResult;
  proposedChanges: ProposedDraftChange[];
  capabilities?: KnowledgeCapabilities;
  progress?: KnowledgeProgressEvent;
  error?: string;
  dirtyPaths: string[];
  reviewConfirmed: boolean;
  uiOptions: ResolvedKnowledgeBuilderUiOptions;
  isSending: boolean;
  sendMessage(
    message: string,
    attachments?: KnowledgeMessageAttachment[],
  ): Promise<void>;
  abortMessage(): void;
  selectDocument(path: string): void;
  updateDocument(path: string, content: string): void;
  saveDocument(path?: string, contentOverride?: string): Promise<void>;
  createDocument(path: string, content?: string): Promise<void>;
  renameDocument(path: string, nextPath: string): Promise<void>;
  deleteDocument(path: string): Promise<void>;
  renameFolder(path: string, nextPath: string): Promise<void>;
  deleteFolder(path: string): Promise<void>;
  uploadFiles(files: File[]): Promise<void>;
  addUrl(url: string): Promise<void>;
  removeSource(sourceId: string): Promise<void>;
  validateSession(): Promise<void>;
  approveProposedChange(
    changeId: string,
    operationIndex?: number,
    options?: { report?: boolean; throwOnError?: boolean },
  ): Promise<void>;
  rejectProposedChange(
    changeId: string,
    operationIndex?: number,
  ): Promise<void>;
  refreshSessionState(): Promise<void>;
  setReviewConfirmed(confirmed: boolean): void;
  dismissError(): Promise<void>;
}

type ResolvedKnowledgeBuilderUiOptions = Required<
  Omit<KnowledgeBuilderUiOptions, 'agent'>
> &
  Pick<KnowledgeBuilderUiOptions, 'agent'>;

const KnowledgeBuilderContext = createContext<
  KnowledgeBuilderContextValue | undefined
>(undefined);

export function useKnowledgeBuilder() {
  const value = useContext(KnowledgeBuilderContext);
  if (!value) {
    throw new Error(
      'useKnowledgeBuilder must be used inside KnowledgeBuilderProvider',
    );
  }
  return value;
}

interface ProviderProps {
  formData?: KnowledgeBuilderFormData;
  onChange(value: KnowledgeBuilderFormData): void;
  uiOptions?: KnowledgeBuilderUiOptions;
  rootFormData?: Record<string, unknown>;
}

const defaultUiOptions: ResolvedKnowledgeBuilderUiOptions = {
  documentSet: 'system',
  templateRef: '',
  sourcePath: '',
  initialDocument: 'docs/index.md',
  definitionSchemaPath: '',
  definitionInterviewInstructionsPath: '',
  valuesTemplatePath: '',
  agent: undefined,
  allowFiles: true,
  allowLinks: true,
  allowCatalogContext: true,
  maxFiles: 10,
  maxFileSizeMb: 20,
  hiddenDocumentPaths: [],
  height: 760,
  contextFields: {},
};

export const KnowledgeBuilderProvider = ({
  children,
  formData,
  onChange,
  uiOptions,
  rootFormData,
}: PropsWithChildren<ProviderProps>) => {
  const api = useApi(knowledgeApiRef);
  const initialFormData = useRef(formData);
  const booted = useRef(false);
  const activeRequest = useRef<AbortController>();
  const sendQueue = useRef<Promise<void>>(Promise.resolve());
  const publishTimer = useRef<ReturnType<typeof setTimeout>>();
  const options = useMemo(
    () => ({ ...defaultUiOptions, ...uiOptions }),
    [uiOptions],
  );
  const [session, setSession] = useState<KnowledgeSession>();
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [directories, setDirectories] = useState<string[]>([]);
  const [messages, setMessages] = useState<KnowledgeMessage[]>([]);
  const [sources, setSources] = useState<KnowledgeSource[]>([]);
  const [activeDocumentPath, setActiveDocumentPath] = useState(
    options.initialDocument,
  );
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [isSending, setIsSending] = useState(false);
  const [dirtyPaths, setDirtyPaths] = useState<string[]>([]);
  const [validation, setValidation] = useState<KnowledgeValidationResult>();
  const [proposedChanges, setProposedChanges] = useState<ProposedDraftChange[]>(
    [],
  );
  const [reviewConfirmed, setReviewConfirmed] = useState(
    initialFormData.current?.reviewConfirmed === true,
  );
  const [capabilities, setCapabilities] = useState<KnowledgeCapabilities>();
  const [error, setError] = useState<string>();
  const [progressOverride, setProgressOverride] =
    useState<KnowledgeProgressEvent>();
  const contextInputKey = stableSerialize({
    rootFormData,
    formContext: formData?.context,
    contextFields: options.contextFields,
  });
  const scaffolderContext = useMemo(
    () => buildScaffolderContext(rootFormData, options, formData),
    [contextInputKey, options, rootFormData, formData],
  );
  const scaffolderContextKey = stableSerialize(scaffolderContext);
  const lastPublishedContext = useRef('');
  const { lastSignal } = useSignal<KnowledgeProgressEvent>(
    session ? `knowledge.session.${session.id}` : 'knowledge.session.pending',
  );

  const storageKey = session?.id
    ? `rdap-knowledge-builder:${session.id}`
    : undefined;

  const publishFormData = useCallback(
    (
      nextSession: KnowledgeSession | undefined,
      nextDocuments: KnowledgeDocument[],
      _nextSources: KnowledgeSource[],
    ) => {
      const readiness = buildKnowledgeReviewReadiness({
        documents: nextDocuments,
        dirtyPaths,
        proposedChanges,
        validation,
      });
      onChange({
        draftId: nextSession?.id,
        version: draftVersion(nextDocuments),
        validationStatus: validationStatusFor(validation),
        reviewConfirmed: reviewConfirmed ? true : undefined,
        review: {
          status: reviewConfirmed
            ? 'Reviewed and confirmed'
            : readiness.ready
            ? 'Ready for user confirmation'
            : 'Review required',
          files: nextDocuments.map(document => document.path).join('\n'),
          validation: validationStatusFor(validation),
          pendingAiChanges: proposedChanges.filter(
            change => change.status === 'pending',
          ).length,
          unsavedDocuments: dirtyPaths.length,
          completenessIssues: readiness.checklistIssues.length,
        },
        // Compatibility for templates that still read parameters.knowledge.sessionId.
        sessionId: nextSession?.id,
        context: nextSession?.focus,
      });
    },
    [dirtyPaths, onChange, proposedChanges, reviewConfirmed, validation],
  );

  const reviewFingerprint = useMemo(
    () =>
      stableSerialize({
        documents: documents.map(document => ({
          path: document.path,
          revision: document.revision,
          content: document.content,
        })),
        dirtyPaths,
        pendingChanges: proposedChanges
          .filter(change => change.status === 'pending')
          .map(change => change.id),
      }),
    [dirtyPaths, documents, proposedChanges],
  );
  const previousReviewFingerprint = useRef(reviewFingerprint);

  useEffect(() => {
    if (previousReviewFingerprint.current === reviewFingerprint) {
      return;
    }
    previousReviewFingerprint.current = reviewFingerprint;
    setReviewConfirmed(false);
    setValidation(undefined);
  }, [reviewFingerprint]);

  useEffect(() => {
    if (booted.current) {
      return undefined;
    }
    booted.current = true;
    let cancelled = false;

    async function boot() {
      setSaveState('saving');
      try {
        let backendConfig: KnowledgeCapabilities | undefined;
        try {
          backendConfig = await api.getConfig();
        } catch (e) {
          console.error('[KnowledgeBuilder] Failed to load backend config', e);
          setError(
            e instanceof Error
              ? e.message
              : 'Knowledge backend config unavailable',
          );
          setSaveState('offline');
        }
        setCapabilities(backendConfig);
        const existingDraftId =
          initialFormData.current?.draftId ??
          initialFormData.current?.sessionId;
        const response = existingDraftId
          ? await api.getSession(existingDraftId)
          : await api.createSession({
              templateRef: options.templateRef,
              documentSet: options.documentSet,
              sourcePath: options.sourcePath,
              initialDocument: options.initialDocument,
              definitionSchemaPath: options.definitionSchemaPath,
              definitionInterviewInstructionsPath:
                options.definitionInterviewInstructionsPath,
              valuesTemplatePath: options.valuesTemplatePath,
              scaffolderParameters: rootFormData,
              agent: options.agent,
              focus: scaffolderContext.focus,
            });

        if (cancelled) {
          return;
        }

        const responseDocuments = response.documents.length
          ? response.documents
          : createInitialDocuments(options.documentSet, response.session.id);
        const responseDirectories = normalizeDirectoryPaths(
          response.directories ?? deriveDirectoryPaths(responseDocuments),
        );
        const activePath =
          response.session.activeDocumentPath &&
          responseDocuments.some(
            document => document.path === response.session.activeDocumentPath,
          )
            ? response.session.activeDocumentPath
            : responseDocuments[0]?.path || options.initialDocument;

        if (!response.documents.length) {
          console.warn(
            '[KnowledgeBuilder] Backend session returned no documents; using local initial document set',
            {
              sessionId: response.session.id,
              documentSet: options.documentSet,
            },
          );
        }
        console.info('[KnowledgeBuilder] Session booted', {
          sessionId: response.session.id,
          documents: responseDocuments.length,
          directories: responseDirectories.length,
          sources: response.sources.length,
          chatProvider: backendConfig?.routes.chat,
        });

        setSession(response.session);
        setDocuments(responseDocuments);
        setDirectories(responseDirectories);
        setMessages(response.messages);
        setSources(response.sources);
        if (response.session.id) {
          setProposedChanges(
            await api.listProposedChanges(response.session.id),
          );
        }
        setActiveDocumentPath(activePath);
        publishFormData(response.session, responseDocuments, response.sources);
        setSaveState('saved');
      } catch (e) {
        if (cancelled) {
          return;
        }
        console.error('[KnowledgeBuilder] Failed to boot session', e);
        const fallback = createFallbackSession(
          options,
          initialFormData.current,
        );
        const saved = loadFallback(fallback.id);
        const fallbackDocuments =
          saved?.documents ??
          createInitialDocuments(options.documentSet, fallback.id);
        setSession(saved?.session ?? fallback);
        setDocuments(fallbackDocuments);
        setDirectories(
          saved?.directories ?? deriveDirectoryPaths(fallbackDocuments),
        );
        setMessages(saved?.messages ?? []);
        setSources(saved?.sources ?? []);
        setProposedChanges([]);
        setActiveDocumentPath(
          saved?.session.activeDocumentPath ||
            saved?.documents?.[0]?.path ||
            options.initialDocument,
        );
        setError(
          e instanceof Error ? e.message : 'Knowledge backend unavailable',
        );
        setSaveState('offline');
      }
    }

    boot();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!session?.id) {
      return;
    }

    if (scaffolderContextKey === lastPublishedContext.current) {
      return;
    }
    lastPublishedContext.current = scaffolderContextKey;

    setSession(current =>
      current
        ? {
            ...current,
            focus: scaffolderContext.focus ?? current.focus,
            updatedAt: now(),
          }
        : current,
    );

    api
      .updateSessionContext(session.id, {
        focus: scaffolderContext.focus,
        templateParameters: scaffolderContext.templateParameters,
      })
      .catch(e => {
        console.warn('[KnowledgeBuilder] Failed to update session context', e);
      });
  }, [api, scaffolderContext, scaffolderContextKey, session?.id]);

  useEffect(() => {
    if (!storageKey || !session) {
      return undefined;
    }

    // documents/messages/sources get a new array reference on every
    // keystroke; persisting to localStorage and republishing the field's
    // value up to the surrounding Scaffolder form on every character typed
    // is expensive and was causing noticeable editing lag. Debounce it.
    const flush = () => {
      persistFallback(storageKey, {
        session,
        documents,
        directories,
        messages,
        sources,
      });
      publishFormData(session, documents, sources);
    };

    if (publishTimer.current) {
      clearTimeout(publishTimer.current);
    }
    publishTimer.current = setTimeout(flush, 400);

    return () => {
      if (publishTimer.current) {
        clearTimeout(publishTimer.current);
        publishTimer.current = undefined;
      }
    };
  }, [
    storageKey,
    session,
    documents,
    directories,
    messages,
    sources,
    publishFormData,
  ]);

  useEffect(() => {
    if (!lastSignal) {
      return;
    }
    setProgressOverride(undefined);
    if (lastSignal.type.endsWith('.failed') && lastSignal.error) {
      setError(lastSignal.error);
    }
    if (
      lastSignal.type === 'source.ingestion.completed' ||
      lastSignal.type === 'assistant.completed' ||
      lastSignal.type === 'source.reindex.completed'
    ) {
      setSaveState('saved');
    }
  }, [lastSignal]);

  const activeDocument = documents.find(
    document => document.path === activeDocumentPath,
  );

  const selectDocument = useCallback((path: string) => {
    setActiveDocumentPath(path);
    setSession(current =>
      current
        ? { ...current, activeDocumentPath: path, updatedAt: now() }
        : current,
    );
  }, []);

  const updateDocument = useCallback(
    (path: string, content: string) => {
      const document = documents.find(item => item.path === path);
      if (!document) {
        return;
      }

      const nextDocument = {
        ...document,
        content,
        status: 'draft' as const,
        updatedAt: now(),
      };

      setDocuments(current =>
        current.map(item => (item.path === path ? nextDocument : item)),
      );
      setDirtyPaths(current => [...new Set([...current, path])]);
      setSaveState('idle');
    },
    [documents],
  );

  const saveDocument = useCallback(
    async (path = activeDocumentPath, contentOverride?: string) => {
      const document = documents.find(item => item.path === path);
      if (!session || !document) {
        return;
      }
      // A caller passing contentOverride is asking us to persist that exact
      // content right now (e.g. a programmatic edit), so it doesn't need to
      // wait on dirtyPaths — which, called synchronously right after the
      // local state update that produced this content, would still reflect
      // the pre-edit value.
      if (
        contentOverride === undefined &&
        !dirtyPaths.includes(document.path)
      ) {
        return;
      }
      const sentContent = contentOverride ?? document.content;
      setSaveState('saving');
      try {
        const updated = await api.updateDocument(
          session.id,
          document.id,
          sentContent,
        );
        // The user may keep typing while this request is in flight. If the
        // live content has since diverged from what we sent, keep the
        // in-editor content and only adopt the server's revision/accepted
        // metadata — otherwise we'd snap the editor back to the older,
        // just-persisted text (see MonacoDocumentEditor's revision-sync
        // effect) and silently drop the newer keystrokes.
        let stillDirty = false;
        setDocuments(current =>
          current.map(item => {
            if (item.id !== updated.id) {
              return item;
            }
            if (item.content !== sentContent) {
              stillDirty = true;
              return {
                ...updated,
                content: item.content,
                status: 'draft' as const,
              };
            }
            return updated;
          }),
        );
        setDirtyPaths(current =>
          stillDirty ? current : current.filter(item => item !== document.path),
        );
        setSaveState(stillDirty ? 'idle' : 'saved');
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Unable to save document');
        setSaveState('offline');
      }
    },
    [activeDocumentPath, api, dirtyPaths, documents, session],
  );

  const createDocument = useCallback(
    async (path: string, content?: string) => {
      if (!session) {
        return;
      }
      try {
        const document = await api.createDocument(session.id, {
          path,
          content,
        });
        setDocuments(current => {
          const next = mergeDocuments(current, [document]);
          setDirectories(directories =>
            normalizeDirectoryPaths(
              mergeDirectoryPaths(directories, deriveDirectoryPaths(next)),
            ),
          );
          return next;
        });
        setActiveDocumentPath(document.path);
        setSaveState('saved');
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Unable to create document');
        setSaveState('offline');
      }
    },
    [api, session],
  );

  const renameDocument = useCallback(
    async (path: string, nextPath: string) => {
      const document = documents.find(item => item.path === path);
      if (!session || !document) {
        return;
      }
      try {
        const updated = await api.renameDocument(
          session.id,
          document.id,
          nextPath,
        );
        setDocuments(current => mergeDocuments(current, [updated]));
        setDirectories(current =>
          normalizeDirectoryPaths(
            current.map(item =>
              item === path || item.startsWith(`${path}/`)
                ? `${nextPath}${item.slice(path.length)}`
                : item,
            ),
          ),
        );
        setDirtyPaths(current =>
          current
            .filter(item => item !== path)
            .concat(dirtyPaths.includes(path) ? [updated.path] : []),
        );
        setActiveDocumentPath(updated.path);
        setSaveState('saved');
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Unable to rename document');
        setSaveState('offline');
      }
    },
    [api, dirtyPaths, documents, session],
  );

  const deleteDocument = useCallback(
    async (path: string) => {
      const document = documents.find(item => item.path === path);
      if (!session || !document) {
        return;
      }
      try {
        await api.deleteDocument(session.id, document.id);
        const remaining = documents.filter(item => item.id !== document.id);
        setDocuments(remaining);
        setDirectories(current => normalizeDirectoryPaths(current));
        setDirtyPaths(current => current.filter(item => item !== path));
        if (activeDocumentPath === path) {
          setActiveDocumentPath(remaining[0]?.path ?? options.initialDocument);
        }
        setSaveState('saved');
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Unable to delete document');
        setSaveState('offline');
      }
    },
    [activeDocumentPath, api, documents, options.initialDocument, session],
  );

  const renameFolder = useCallback(
    async (path: string, nextPath: string) => {
      if (!session) {
        return;
      }
      const prefix = `${path.replace(/\/$/, '')}/`;
      const nextPrefix = `${nextPath.replace(/\/$/, '')}/`;
      try {
        const updated = await api.renameFolder(session.id, path, nextPath);
        setDocuments(current => {
          const next = mergeDocuments(
            current.filter(document => !document.path.startsWith(prefix)),
            updated,
          );
          setDirectories(directories =>
            normalizeDirectoryPaths(
              directories.map(item =>
                item === path || item.startsWith(prefix)
                  ? `${nextPath}${item.slice(path.length)}`
                  : item,
              ),
            ),
          );
          return next;
        });
        setDirtyPaths(current =>
          current.map(item =>
            item.startsWith(prefix)
              ? `${nextPrefix}${item.slice(prefix.length)}`
              : item,
          ),
        );
        if (activeDocumentPath?.startsWith(prefix)) {
          setActiveDocumentPath(
            `${nextPrefix}${activeDocumentPath.slice(prefix.length)}`,
          );
        }
        setSaveState('saved');
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Unable to rename folder');
        setSaveState('offline');
      }
    },
    [activeDocumentPath, api, session],
  );

  const deleteFolder = useCallback(
    async (path: string) => {
      if (!session) {
        return;
      }
      const prefix = `${path.replace(/\/$/, '')}/`;
      try {
        await api.deleteFolder(session.id, path);
        const remaining = documents.filter(
          document => !document.path.startsWith(prefix),
        );
        setDocuments(remaining);
        setDirectories(current =>
          normalizeDirectoryPaths(
            current.filter(
              item => item !== path && !item.startsWith(`${path}/`),
            ),
          ),
        );
        setDirtyPaths(current =>
          current.filter(item => !item.startsWith(prefix)),
        );
        if (activeDocumentPath?.startsWith(prefix)) {
          setActiveDocumentPath(remaining[0]?.path ?? options.initialDocument);
        }
        setSaveState('saved');
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Unable to delete folder');
        setSaveState('offline');
      }
    },
    [activeDocumentPath, api, documents, options.initialDocument, session],
  );

  const sendMessage = useCallback(
    (content: string, attachments?: KnowledgeMessageAttachment[]) => {
      const run = async () => {
        if (!session || !content.trim()) {
          return;
        }

        await saveDocument(activeDocumentPath);

        if (!isRealAssistantConfigured(capabilities, options)) {
          const message =
            'No LLM provider is configured for Knowledge Builder chat. Configure an OpenAI or Bedrock provider before sending messages.';
          console.warn('[KnowledgeBuilder] Send blocked: no LLM provider', {
            sessionId: session.id,
            chatRoute: capabilities?.routes.chat,
            providers: capabilities?.providers,
          });
          setError(message);
          setSaveState('error');
          setMessages(current => [
            ...current,
            {
              id: `local-error-${Date.now()}`,
              sessionId: session.id,
              role: 'system',
              content: message,
              createdAt: now(),
            },
          ]);
          return;
        }

        const userMessage: KnowledgeMessage = {
          id: `local-message-${Date.now()}`,
          sessionId: session.id,
          role: 'user',
          content,
          attachments,
          createdAt: now(),
        };
        setMessages(current => [...current, userMessage]);

        const controller = new AbortController();
        activeRequest.current = controller;
        const requestStartedAt = Date.now();

        try {
          console.info('[KnowledgeBuilder] Sending chat message', {
            sessionId: session.id,
            activeDocumentPath,
          });
          const response = await api.sendMessage(
            session.id,
            content,
            activeDocumentPath,
            options.agent,
            controller.signal,
            attachments,
          );
          const durationMs = Date.now() - requestStartedAt;
          console.info('[KnowledgeBuilder] Chat message completed', {
            sessionId: session.id,
            proposedChangeId: response.proposedChange?.id,
            durationMs,
          });
          setMessages(current => [
            ...current,
            { ...response.message, durationMs },
          ]);
          if (response.updatedDocuments.length) {
            setDocuments(current =>
              mergeDocuments(current, response.updatedDocuments),
            );
          }
          if (response.proposedChange) {
            setProposedChanges(current => [
              response.proposedChange!,
              ...current.filter(
                change => change.id !== response.proposedChange!.id,
              ),
            ]);
          }
          setSaveState('saved');
        } catch (e) {
          if (controller.signal.aborted) {
            setMessages(current => [
              ...current,
              {
                id: `local-error-${Date.now()}`,
                sessionId: session.id,
                role: 'system',
                content: 'Generation stopped.',
                createdAt: now(),
              },
            ]);
            setSaveState('saved');
            return;
          }
          console.error('[KnowledgeBuilder] Chat message failed', e);
          const message =
            e instanceof Error ? e.message : 'Unable to send message';
          setMessages(current => [
            ...current,
            {
              id: `local-error-${Date.now()}`,
              sessionId: session.id,
              role: 'system',
              content: message,
              createdAt: now(),
            },
          ]);
          setError(message);
          setSaveState('offline');
        } finally {
          if (activeRequest.current === controller) {
            activeRequest.current = undefined;
          }
        }
      };

      const next = sendQueue.current.then(async () => {
        setIsSending(true);
        try {
          await run();
        } finally {
          setIsSending(false);
        }
      });
      sendQueue.current = next.catch(() => {});
      return next;
    },
    [activeDocumentPath, api, capabilities, options, saveDocument, session],
  );

  const abortMessage = useCallback(() => {
    activeRequest.current?.abort();
    if (session) {
      api.failActiveRuns(session.id, 'Cancelled by user').catch(e => {
        console.warn('[KnowledgeBuilder] Failed to mark active runs failed', e);
      });
    }
  }, [api, session]);

  const addUrl = useCallback(
    async (url: string) => {
      if (!session || !url.trim()) {
        return;
      }
      const normalizedUrl = normalizeHttpUrl(url);
      if (!normalizedUrl) {
        const message = 'Enter a valid URL starting with http:// or https://.';
        setError(message);
        setSaveState('error');
        console.warn('[KnowledgeBuilder] URL source rejected by client', {
          sessionId: session.id,
          url,
        });
        return;
      }
      console.info('[KnowledgeBuilder] Adding URL source', {
        sessionId: session.id,
        url: normalizedUrl,
      });
      const sourceDocument = createUrlDocument(session.id, normalizedUrl);
      setDocuments(
        current =>
          upsertTemplateLinkDocument(
            upsertSourceDocument(current, sourceDocument),
            session.id,
            normalizedUrl,
          ).documents,
      );
      setActiveDocumentPath(sourceDocument.path);
      try {
        const source = await api.addUrl(session.id, { url: normalizedUrl });
        const refreshed = await api.getSession(session.id);
        const linkedTemplate = upsertTemplateLinkDocument(
          refreshed.documents,
          session.id,
          normalizedUrl,
        );
        let nextDocuments = linkedTemplate.documents;
        if (linkedTemplate.document) {
          const persisted = linkedTemplate.created
            ? await api.createDocument(session.id, {
                path: linkedTemplate.document.path,
                title: linkedTemplate.document.title,
                content: linkedTemplate.document.content,
              })
            : await api.updateDocument(
                session.id,
                linkedTemplate.document.id,
                linkedTemplate.document.content,
              );
          nextDocuments = mergeDocuments(nextDocuments, [persisted]);
        }
        setSources(current => [...current, source]);
        setDocuments(nextDocuments);
        setDirectories(current =>
          normalizeDirectoryPaths(
            mergeDirectoryPaths(
              current,
              refreshed.directories ?? deriveDirectoryPaths(nextDocuments),
            ),
          ),
        );
        setActiveDocumentPath('sources/urls.md');
      } catch (e) {
        console.error('[KnowledgeBuilder] URL source failed', e);
        setDocuments(current =>
          current.filter(document => document.path !== sourceDocument.path),
        );
        setError(e instanceof Error ? e.message : 'Unable to add URL');
        setSaveState('offline');
      }
    },
    [api, session],
  );

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!session) {
        return;
      }

      const acceptedFiles = files.slice(0, options.maxFiles);
      for (const file of acceptedFiles) {
        if (file.size > options.maxFileSizeMb * 1024 * 1024) {
          setError(`${file.name} exceeds ${options.maxFileSizeMb} MB`);
          continue;
        }

        const content = await readFilePreview(file);
        const sourceId = `local-file-${Date.now()}-${file.name}`;
        console.info('[KnowledgeBuilder] Uploading file source', {
          sessionId: session.id,
          fileName: file.name,
          size: file.size,
          mimeType: file.type,
        });
        const sourceDocument = createFileDocument({
          sessionId: session.id,
          sourceId,
          fileName: file.name,
          mimeType: file.type,
          content,
        });
        setDocuments(current => upsertSourceDocument(current, sourceDocument));
        setActiveDocumentPath(sourceDocument.path);

        try {
          const source = await api.uploadFile(session.id, file);
          const refreshed = await api.getSession(session.id);
          const persistedPath = sourceDocumentPath(
            file.name,
            source.id,
            file.type,
          );
          const persistedDocument = refreshed.documents.find(
            document => document.path === persistedPath,
          );
          const nextDocuments = mergeDocuments(refreshed.documents, [
            {
              ...sourceDocument,
              id: persistedDocument?.id ?? sourceDocument.id,
              documentId:
                persistedDocument?.documentId ?? sourceDocument.documentId,
              path: persistedPath,
              revision: persistedDocument?.revision ?? sourceDocument.revision,
              acceptedRevision:
                persistedDocument?.acceptedRevision ??
                sourceDocument.acceptedRevision,
            },
          ]);
          setSources(current => [...current, source]);
          setDocuments(nextDocuments);
          setDirectories(current =>
            normalizeDirectoryPaths(
              mergeDirectoryPaths(
                current,
                refreshed.directories ?? deriveDirectoryPaths(nextDocuments),
              ),
            ),
          );
          setActiveDocumentPath(persistedPath);
        } catch (e) {
          console.error('[KnowledgeBuilder] File source upload failed', e);
          setDocuments(current =>
            current.filter(document => document.id !== sourceDocument.id),
          );
          setActiveDocumentPath(current =>
            current === sourceDocument.path ? options.initialDocument : current,
          );
          setError(
            e instanceof Error ? e.message : `Unable to upload ${file.name}`,
          );
          setSaveState('offline');
        }
      }
    },
    [api, options.maxFileSizeMb, options.maxFiles, session],
  );

  const removeSource = useCallback(
    async (sourceId: string) => {
      setSources(current => current.filter(source => source.id !== sourceId));
      if (session) {
        try {
          await api.removeSource(session.id, sourceId);
        } catch {
          setSaveState('offline');
        }
      }
    },
    [api, session],
  );

  const validateSession = useCallback(async () => {
    if (!session) {
      return;
    }

    try {
      setValidation(await api.validateSession(session.id));
    } catch {
      const errors = documents
        .filter(document => !document.content.trim())
        .map(document => ({
          document: document.path,
          code: 'EMPTY_DOCUMENT',
          message: 'Document has no content.',
        }));
      setValidation({ valid: errors.length === 0, errors, warnings: [] });
      setSaveState('offline');
    }
  }, [api, documents, session]);

  const refreshSessionState = useCallback(async () => {
    if (!session?.id) {
      return;
    }
    try {
      const [response, changes] = await Promise.all([
        api.getSession(session.id),
        api.listProposedChanges(session.id),
      ]);
      setSession(response.session);
      setDocuments(response.documents);
      setDirectories(
        normalizeDirectoryPaths(
          response.directories ?? deriveDirectoryPaths(response.documents),
        ),
      );
      setMessages(response.messages);
      setSources(response.sources);
      setProposedChanges(changes);
      setDirtyPaths([]);
      setSaveState('saved');
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Unable to refresh Knowledge Builder',
      );
      setSaveState('offline');
    }
  }, [api, session?.id]);

  const sendReviewReport = useCallback(
    (change: ProposedDraftChange) => {
      void sendMessage(describeReviewedChange(change));
    },
    [sendMessage],
  );

  const approveProposedChange = useCallback(
    async (
      changeId: string,
      operationIndex?: number,
      approveOptions?: { report?: boolean; throwOnError?: boolean },
    ) => {
      if (!session) {
        if (approveOptions?.throwOnError) throw new Error('No active session');
        return;
      }
      setSaveState('saving');
      try {
        const response = await api.approveProposedChange(
          session.id,
          changeId,
          operationIndex,
        );
        setDocuments(current =>
          mergeDocuments(current, response.updatedDocuments),
        );
        setProposedChanges(current =>
          current.map(change =>
            change.id === response.change.id ? response.change : change,
          ),
        );
        setSaveState('saved');
        if (
          approveOptions?.report !== false &&
          response.change.status !== 'pending'
        ) {
          sendReviewReport(response.change);
        }
      } catch (e) {
        const message = e instanceof Error ? e.message : undefined;
        setError(
          message === 'Draft changed after this proposal was created'
            ? 'This proposal is out of date because the draft changed since it was created. Ask the assistant to regenerate it and try again.'
            : message ?? 'Unable to approve proposed change',
        );
        setSaveState('error');
        setProposedChanges(await api.listProposedChanges(session.id));
        if (approveOptions?.throwOnError) throw e;
      }
    },
    [api, sendReviewReport, session],
  );

  const rejectProposedChange = useCallback(
    async (changeId: string, operationIndex?: number) => {
      if (!session) {
        return;
      }
      try {
        const change = await api.rejectProposedChange(
          session.id,
          changeId,
          operationIndex,
        );
        setProposedChanges(current =>
          current.map(item => (item.id === change.id ? change : item)),
        );
        setSaveState('saved');
        if (change.status !== 'pending') {
          sendReviewReport(change);
        }
      } catch (e) {
        setError(
          e instanceof Error ? e.message : 'Unable to reject proposed change',
        );
        setSaveState('error');
      }
    },
    [api, sendReviewReport, session],
  );

  const dismissError = useCallback(async () => {
    const message = error;
    setError(undefined);
    setSaveState(current => (current === 'offline' ? 'error' : current));

    if (session) {
      setProgressOverride({
        type: 'assistant.failed',
        sessionId: session.id,
        runId: lastSignal?.runId,
        workload: lastSignal?.workload,
        providerId: lastSignal?.providerId,
        error: message ?? 'Assistant error dismissed by user',
      });
    }

    if (!session) {
      return;
    }

    try {
      await api.failActiveRuns(session.id, message);
    } catch (e) {
      console.warn('[KnowledgeBuilder] Failed to mark active runs failed', e);
    }
  }, [api, error, lastSignal, session]);

  const value = useMemo(
    () => ({
      session,
      activeDocument,
      activeDocumentPath,
      messages,
      sources,
      documents,
      directories,
      saveState,
      validation,
      proposedChanges,
      capabilities,
      progress: progressOverride ?? lastSignal ?? undefined,
      error,
      dirtyPaths,
      reviewConfirmed,
      uiOptions: options,
      isSending,
      sendMessage,
      abortMessage,
      selectDocument,
      updateDocument,
      saveDocument,
      createDocument,
      renameDocument,
      deleteDocument,
      renameFolder,
      deleteFolder,
      uploadFiles,
      addUrl,
      removeSource,
      validateSession,
      approveProposedChange,
      rejectProposedChange,
      refreshSessionState,
      setReviewConfirmed,
      dismissError,
    }),
    [
      abortMessage,
      activeDocument,
      activeDocumentPath,
      addUrl,
      approveProposedChange,
      capabilities,
      createDocument,
      deleteDocument,
      deleteFolder,
      dismissError,
      directories,
      documents,
      dirtyPaths,
      error,
      isSending,
      lastSignal,
      messages,
      options,
      progressOverride,
      proposedChanges,
      reviewConfirmed,
      rejectProposedChange,
      refreshSessionState,
      removeSource,
      renameDocument,
      renameFolder,
      saveState,
      saveDocument,
      selectDocument,
      sendMessage,
      session,
      sources,
      updateDocument,
      uploadFiles,
      validateSession,
      validation,
    ],
  );

  const chatContextValue = useMemo(
    () => ({
      session,
      messages,
      sendMessage,
      abortMessage,
      uploadFiles,
      isSending,
    }),
    [session, messages, sendMessage, abortMessage, uploadFiles, isSending],
  );

  return (
    <KnowledgeBuilderContext.Provider value={value}>
      <KnowledgeChatContext.Provider value={chatContextValue}>
        {children}
      </KnowledgeChatContext.Provider>
    </KnowledgeBuilderContext.Provider>
  );
};

function now() {
  return new Date().toISOString();
}

function describeReviewedChange(change: ProposedDraftChange): string {
  const lines = change.operations.map(operation => {
    const outcome = operation.status === 'approved' ? 'Approved' : 'Rejected';
    const label =
      operation.type === 'renameFile'
        ? `${operation.type}: ${operation.from} -> ${operation.to}`
        : `${operation.type}: ${operation.path}`;
    return `- ${outcome} — ${label}`;
  });
  return [
    'I finished reviewing the proposed changes from your last response:',
    ...lines,
    'Please continue accordingly.',
  ].join('\n');
}

function createFallbackSession(
  options: ResolvedKnowledgeBuilderUiOptions,
  formData?: KnowledgeBuilderFormData,
): KnowledgeSession {
  const createdAt = now();
  return {
    id: formData?.draftId ?? formData?.sessionId ?? `local-${Date.now()}`,
    userRef: 'user:default/local',
    status: 'active',
    kind: 'draft',
    focus: formData?.context,
    documentSet: options.documentSet,
    activeDocumentPath: options.initialDocument,
    createdAt,
    updatedAt: createdAt,
    expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
  };
}

function loadFallback(sessionId: string) {
  const stored = sessionStorage.getItem(`rdap-knowledge-builder:${sessionId}`);
  if (!stored) {
    return undefined;
  }
  try {
    return JSON.parse(stored) as {
      session: KnowledgeSession;
      documents: KnowledgeDocument[];
      directories?: string[];
      messages: KnowledgeMessage[];
      sources: KnowledgeSource[];
    };
  } catch {
    return undefined;
  }
}

function persistFallback(
  storageKey: string,
  value: {
    session: KnowledgeSession;
    documents: KnowledgeDocument[];
    directories: string[];
    messages: KnowledgeMessage[];
    sources: KnowledgeSource[];
  },
) {
  const serialized = JSON.stringify({
    session: value.session,
    documents: compactDocumentsForStorage(value.documents),
    directories: value.directories,
    messages: value.messages.slice(-20),
    sources: value.sources,
  });

  try {
    sessionStorage.setItem(storageKey, serialized);
  } catch (e) {
    try {
      sessionStorage.removeItem(storageKey);
      sessionStorage.setItem(storageKey, serialized);
    } catch {
      console.warn('[KnowledgeBuilder] Browser draft cache skipped', e);
    }
  }
}

function compactDocumentsForStorage(documents: KnowledgeDocument[]) {
  let remainingContentBudget = 200_000;

  return documents.map(document => {
    const content = compactStoredContent(
      document.content,
      remainingContentBudget,
    );
    remainingContentBudget -= content.length;

    const acceptedContent =
      document.acceptedContent === document.content
        ? content
        : compactStoredContent(
            document.acceptedContent,
            remainingContentBudget,
          );
    remainingContentBudget -= acceptedContent.length;

    return {
      ...document,
      content,
      acceptedContent,
    };
  });
}

function compactStoredContent(content: string, remainingContentBudget: number) {
  if (
    remainingContentBudget <= 0 ||
    content.startsWith('data:') ||
    content.length > 50_000
  ) {
    return '';
  }

  return content;
}

function deriveDirectoryPaths(documents: KnowledgeDocument[]) {
  const directories = new Set<string>();
  for (const document of documents) {
    const parts = document.path.split('/');
    parts.pop();
    let current = '';
    for (const part of parts) {
      current = current ? `${current}/${part}` : part;
      directories.add(current);
    }
  }
  return Array.from(directories).sort();
}

function mergeDirectoryPaths(current: string[], next: string[]) {
  return Array.from(new Set([...current, ...next])).sort();
}

function normalizeDirectoryPaths(directories: string[]) {
  return Array.from(
    new Set(
      directories
        .map(directory =>
          directory.trim().replace(/^\/+/, '').replace(/\/+/g, '/'),
        )
        .filter(Boolean),
    ),
  ).sort();
}

function createInitialDocuments(
  _documentSet: string,
  sessionId = 'local',
): KnowledgeDocument[] {
  const baseDocumentPaths = ['mkdocs.yml', 'docs/index.md'];

  const createdAt = now();
  return baseDocumentPaths.map(path => {
    const title = titleFromPath(path);
    return {
      id: `local-document-${path}`,
      documentId: `local-document-${path}`,
      sessionId,
      path,
      title,
      type: path.includes('/adr/') ? 'adr' : title.toLowerCase(),
      content: defaultContentFor(path, title),
      acceptedContent: defaultContentFor(path, title),
      revision: 1,
      acceptedRevision: 1,
      status: 'accepted',
      createdAt,
      updatedAt: createdAt,
    };
  });
}

function titleFromPath(path: string) {
  const name =
    path
      .split('/')
      .pop()
      ?.replace(/\.(md|ya?ml|json|css|svg|png|pdf|docx?|xlsx?)$/i, '') ||
    'Document';
  return name
    .split('-')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function defaultContentFor(path: string, title: string) {
  if (path === 'mkdocs.yml') {
    return `site_name: Knowledge Workspace

repo_url: ''
repo_name: Knowledge Workspace

theme:
  name: material
  palette:
    - media: "(prefers-color-scheme: light)"
      scheme: default
      primary: custom
      accent: custom
  logo: Images/logo-icon.png
  features:
    - navigation.tabs

nav:
  - Home: index.md

extra_css:
  - stylesheets/styles.css

markdown_extensions:
  - attr_list
  - md_in_html
  - admonition
  - pymdownx.details

plugins:
  - mike
  - search

extra:
  version:
    provider: mike
`;
  }
  if (path === 'docs/index.md') {
    return '# Knowledge Workspace\n\nStart documenting this template here.\n';
  }
  if (/\.(ya?ml|json)$/i.test(path)) {
    return '';
  }
  return `# ${title}\n\n`;
}

function mergeDocuments(
  current: KnowledgeDocument[],
  updates: KnowledgeDocument[],
) {
  return [
    ...current.map(document => {
      const update = updates.find(
        item => item.id === document.id || item.path === document.path,
      );
      return update ?? document;
    }),
    ...updates.filter(
      update =>
        !current.some(
          document =>
            document.id === update.id || document.path === update.path,
        ),
    ),
  ].sort((a, b) => a.path.localeCompare(b.path));
}

function draftVersion(documents: KnowledgeDocument[]) {
  return documents.reduce(
    (version, document) => version + document.revision,
    0,
  );
}

function validationStatusFor(
  validation: KnowledgeValidationResult | undefined,
): 'unknown' | 'valid' | 'invalid' {
  if (validation?.valid === undefined) {
    return 'unknown';
  }
  return validation.valid ? 'valid' : 'invalid';
}

function isRealAssistantConfigured(
  capabilities: KnowledgeCapabilities | undefined,
  options: ResolvedKnowledgeBuilderUiOptions,
) {
  if (!capabilities) {
    return false;
  }
  const defaultAgent = capabilities.agents?.find(
    agent => agent.id === capabilities.defaultAgent,
  );
  const chatProviderId =
    options.agent?.provider ??
    defaultAgent?.provider ??
    capabilities.routes.chat;
  const provider = capabilities.providers.find(
    item => item.id === chatProviderId,
  );
  return Boolean(provider && provider.type !== 'deterministic');
}

function createUrlDocument(
  sessionId: string,
  url: string,
  updatedAt = now(),
): KnowledgeDocument {
  const path = 'sources/urls.md';
  return {
    id: `source-document-${path}`,
    documentId: `source-document-${path}`,
    sessionId,
    path,
    title: 'URLs',
    type: 'source-url',
    content: `# URLs\n\n- [${url}](${url})\n`,
    acceptedContent: `# URLs\n\n- [${url}](${url})\n`,
    revision: 1,
    acceptedRevision: 1,
    status: 'accepted',
    createdAt: updatedAt,
    updatedAt,
  };
}

function createFileDocument(options: {
  sessionId: string;
  sourceId: string;
  fileName: string;
  mimeType?: string;
  content: string;
}): KnowledgeDocument {
  const updatedAt = now();
  const path = sourceDocumentPath(
    options.fileName,
    options.sourceId,
    options.mimeType,
  );
  const body = options.content.trim()
    ? options.content
    : `Uploaded source file: ${options.fileName}`;
  const content = path.endsWith('.md')
    ? `# ${options.fileName}\n\n${body}\n`
    : body;
  return {
    id: `source-document-${options.sourceId}`,
    documentId: `source-document-${options.sourceId}`,
    sessionId: options.sessionId,
    path,
    title: options.fileName,
    type: 'source-file',
    content,
    acceptedContent: content,
    revision: 1,
    acceptedRevision: 1,
    status: 'accepted',
    createdAt: updatedAt,
    updatedAt,
  };
}

function upsertSourceDocument(
  current: KnowledgeDocument[],
  document: KnowledgeDocument,
) {
  const existing = current.find(item => item.path === document.path);
  if (!existing) {
    return [...current, document].sort((a, b) => a.path.localeCompare(b.path));
  }
  const content =
    document.path === 'sources/urls.md'
      ? mergeUrlContent(existing.content, document.content)
      : document.content;
  return current.map(item =>
    item.path === document.path
      ? {
          ...item,
          ...document,
          id: item.id,
          documentId: item.documentId,
          content,
          acceptedContent: content,
          revision: item.revision + 1,
          acceptedRevision: item.revision + 1,
        }
      : item,
  );
}

function upsertTemplateLinkDocument(
  current: KnowledgeDocument[],
  sessionId: string,
  url: string,
): {
  documents: KnowledgeDocument[];
  document?: KnowledgeDocument;
  created: boolean;
} {
  const template =
    current.find(document => document.path === 'template.yaml') ??
    current.find(document => document.path === 'catalog/template.yaml');
  const updatedAt = now();
  if (!template) {
    const document = createTemplateDocumentWithLink(sessionId, url, updatedAt);
    return {
      documents: mergeDocuments(current, [document]),
      document,
      created: true,
    };
  }

  const content = addMetadataLinkToYaml(template.content, url);
  if (content === template.content) {
    return { documents: current, created: false };
  }

  const document = {
    ...template,
    content,
    acceptedContent: content,
    revision: template.revision + 1,
    acceptedRevision: template.revision + 1,
    status: 'accepted' as const,
    updatedAt,
  };
  return {
    documents: mergeDocuments(current, [document]),
    document,
    created: false,
  };
}

function createTemplateDocumentWithLink(
  sessionId: string,
  url: string,
  updatedAt: string,
): KnowledgeDocument {
  const content = `apiVersion: scaffolder.backstage.io/v1beta3
kind: Template
metadata:
  name: knowledge-template
  links:
    - url: ${url}
      title: ${titleForLink(url)}
spec: {}
`;
  return {
    id: 'local-document-template.yaml',
    documentId: 'local-document-template.yaml',
    sessionId,
    path: 'template.yaml',
    title: 'Template',
    type: 'template',
    content,
    acceptedContent: content,
    revision: 1,
    acceptedRevision: 1,
    status: 'accepted',
    createdAt: updatedAt,
    updatedAt,
  };
}

function addMetadataLinkToYaml(content: string, url: string) {
  if (content.includes(`url: ${url}`) || content.includes(`url: "${url}"`)) {
    return content;
  }

  const lines = content.replace(/\s*$/, '').split('\n');
  const metadataIndex = lines.findIndex(line => /^metadata:\s*$/.test(line));
  const linkLines = [
    `  links:`,
    `    - url: ${url}`,
    `      title: ${titleForLink(url)}`,
  ];

  if (metadataIndex === -1) {
    const insertAt = findTopLevelSpecIndex(lines);
    const metadataLines = ['metadata:', ...linkLines];
    if (insertAt === -1) {
      return `${lines.join('\n')}\n${metadataLines.join('\n')}\n`;
    }
    return [
      ...lines.slice(0, insertAt),
      ...metadataLines,
      ...lines.slice(insertAt),
    ]
      .join('\n')
      .concat('\n');
  }

  const metadataEnd = findNextTopLevelIndex(lines, metadataIndex + 1);
  const metadataLines = lines.slice(
    metadataIndex + 1,
    metadataEnd === -1 ? undefined : metadataEnd,
  );
  const linksRelativeIndex = metadataLines.findIndex(line =>
    /^  links:\s*$/.test(line),
  );

  if (linksRelativeIndex === -1) {
    const insertAt = metadataEnd === -1 ? lines.length : metadataEnd;
    return [...lines.slice(0, insertAt), ...linkLines, ...lines.slice(insertAt)]
      .join('\n')
      .concat('\n');
  }

  const linksIndex = metadataIndex + 1 + linksRelativeIndex;
  const linksEnd = findNextMetadataChildIndex(lines, linksIndex + 1);
  const insertAt = linksEnd === -1 ? lines.length : linksEnd;
  return [
    ...lines.slice(0, insertAt),
    `    - url: ${url}`,
    `      title: ${titleForLink(url)}`,
    ...lines.slice(insertAt),
  ]
    .join('\n')
    .concat('\n');
}

function findTopLevelSpecIndex(lines: string[]) {
  return lines.findIndex(line => /^spec:\s*$/.test(line));
}

function findNextTopLevelIndex(lines: string[], start: number) {
  for (let index = start; index < lines.length; index += 1) {
    if (/^[A-Za-z0-9_-]+:\s*/.test(lines[index])) {
      return index;
    }
  }
  return -1;
}

function findNextMetadataChildIndex(lines: string[], start: number) {
  for (let index = start; index < lines.length; index += 1) {
    if (
      /^  [A-Za-z0-9_-]+:\s*/.test(lines[index]) ||
      /^[A-Za-z0-9_-]+:\s*/.test(lines[index])
    ) {
      return index;
    }
  }
  return -1;
}

function titleForLink(url: string) {
  try {
    return new URL(url).hostname;
  } catch {
    return 'Link';
  }
}

function mergeUrlContent(previous: string, next: string) {
  const lines = new Set(
    [...previous.split('\n'), ...next.split('\n')].filter(Boolean),
  );
  return `${[...lines].join('\n')}\n`;
}

function normalizeHttpUrl(value: string) {
  try {
    const parsed = new URL(value.trim());
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return undefined;
    }
    return parsed.toString();
  } catch {
    return undefined;
  }
}

async function readFilePreview(file: File) {
  if (isDataUrlBackedFile(file.type, file.name)) {
    return readDataUrl(file);
  }
  if (
    file.type.startsWith('text/') ||
    file.name.endsWith('.md') ||
    file.name.endsWith('.txt') ||
    file.name.endsWith('.svg') ||
    file.name.endsWith('.css')
  ) {
    return file.text();
  }
  return `Binary source file: ${file.name}\nMIME type: ${
    file.type || 'unknown'
  }\nSize: ${file.size} bytes`;
}

function safeSourceFileName(fileName: string, sourceId: string) {
  const base = fileName.replace(/\.[^.]+$/, '');
  const name =
    base
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'uploaded-source';
  return `${name}-${sourceId
    .slice(-8)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')}`;
}

function sourceDocumentPath(fileName: string, sourceId: string, mimeType = '') {
  const extension = sourceExtension(fileName, mimeType);
  const folder = extension.replace(/^\./, '') || 'other';
  return `sources/files/${folder}/${safeSourceFileName(
    fileName,
    sourceId,
  )}${extension}`;
}

function sourceExtension(fileName: string, mimeType: string) {
  if (mimeType === 'image/png' || /\.png$/i.test(fileName)) {
    return '.png';
  }
  if (mimeType === 'image/svg+xml' || /\.svg$/i.test(fileName)) {
    return '.svg';
  }
  if (mimeType === 'text/css' || /\.css$/i.test(fileName)) {
    return '.css';
  }
  if (mimeType === 'text/html' || /\.html?$/i.test(fileName)) {
    return '.html';
  }
  if (mimeType === 'application/pdf' || /\.pdf$/i.test(fileName)) {
    return '.pdf';
  }
  if (
    mimeType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    /\.docx$/i.test(fileName)
  ) {
    return '.docx';
  }
  if (mimeType === 'application/msword' || /\.doc$/i.test(fileName)) {
    return '.doc';
  }
  if (
    mimeType ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    /\.xlsx$/i.test(fileName)
  ) {
    return '.xlsx';
  }
  if (mimeType === 'application/json' || /\.json$/i.test(fileName)) {
    return '.json';
  }
  if (mimeType === 'application/vnd.ms-excel' || /\.xls$/i.test(fileName)) {
    return '.xls';
  }
  return '.md';
}

function isDataUrlBackedFile(mimeType: string, fileName: string) {
  return (
    mimeType === 'image/png' ||
    mimeType === 'application/pdf' ||
    mimeType === 'application/msword' ||
    mimeType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    mimeType === 'application/vnd.ms-excel' ||
    mimeType ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    /\.(png|pdf|docx?|xlsx?)$/i.test(fileName)
  );
}

function readDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(String(reader.result ?? '')));
    reader.addEventListener('error', () => reject(reader.error));
    reader.readAsDataURL(file);
  });
}

export function buildScaffolderContext(
  rootFormData: Record<string, unknown> | undefined,
  options: { contextFields?: KnowledgeBuilderUiOptions['contextFields'] },
  formData?: KnowledgeBuilderFormData,
) {
  const templateParameters = pickTemplateParameters(rootFormData, options);
  const contextFields = options.contextFields;
  const focus = removeUndefined({
    ...formData?.context,
    kind:
      readMappedString(rootFormData, contextFields?.kind) ??
      formData?.context?.kind,
    entityRef:
      readMappedString(rootFormData, contextFields?.entityRef) ??
      formData?.context?.entityRef,
    tenantRef:
      readMappedString(rootFormData, contextFields?.tenantRef) ??
      readString(rootFormData, 'tenant') ??
      formData?.context?.tenantRef,
    domainRef:
      readMappedString(rootFormData, contextFields?.domainRef) ??
      readString(rootFormData, 'domain') ??
      readString(rootFormData, 'domainName') ??
      formData?.context?.domainRef,
    systemRef:
      readMappedString(rootFormData, contextFields?.systemRef) ??
      readString(rootFormData, 'system') ??
      readString(rootFormData, 'systemName') ??
      formData?.context?.systemRef,
    componentRef:
      readMappedString(rootFormData, contextFields?.componentRef) ??
      readString(rootFormData, 'component') ??
      formData?.context?.componentRef,
  });

  return {
    focus: Object.keys(focus).length ? focus : undefined,
    templateParameters,
  };
}

function pickTemplateParameters(
  rootFormData: Record<string, unknown> | undefined,
  options: { contextFields?: KnowledgeBuilderUiOptions['contextFields'] },
): KnowledgeBuilderTemplateParameters | undefined {
  if (!rootFormData) {
    return undefined;
  }

  const defaultFields = [
    'tenant',
    'domain',
    'domainName',
    'system',
    'systemName',
    'owner',
    'teamId',
    'team_id',
    'name',
    'template_name',
    'repo_name',
    'description',
  ];
  const fields = [
    ...defaultFields,
    ...(options.contextFields?.templateParameters ?? []),
  ];
  const parameters = removeUndefined(
    Object.fromEntries(
      fields.map(field => [field, readPath(rootFormData, field)]),
    ),
  );
  if (parameters.name === undefined && parameters.template_name !== undefined) {
    parameters.name = parameters.template_name;
  }
  if (parameters.template_name === undefined && parameters.name !== undefined) {
    parameters.template_name = parameters.name;
  }

  return Object.keys(parameters).length ? parameters : undefined;
}

function readMappedString(
  rootFormData: Record<string, unknown> | undefined,
  path: string | undefined,
) {
  return path ? asString(readPath(rootFormData, path)) : undefined;
}

function readString(
  rootFormData: Record<string, unknown> | undefined,
  path: string,
) {
  return asString(readPath(rootFormData, path));
}

function readPath(
  source: Record<string, unknown> | undefined,
  path: string,
): unknown {
  return path.split('.').reduce<unknown>((current, segment) => {
    if (!current || typeof current !== 'object') {
      return undefined;
    }
    return (current as Record<string, unknown>)[segment];
  }, source);
}

function asString(value: unknown) {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function removeUndefined<T extends Record<string, unknown>>(value: T) {
  return Object.fromEntries(
    Object.entries(value).filter(([, item]) => item !== undefined),
  ) as Partial<T>;
}

function stableSerialize(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableSerialize).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    return `{${Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => `${JSON.stringify(key)}:${stableSerialize(item)}`)
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? 'undefined';
}
